
import { useState, useRef, useEffect } from 'react';

interface IAMFeature {
  title: string;
  description: string;
  icon: string;
  benefits: string[];
  image: string;
}

export default function IdentityManagement() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const iamFeatures: IAMFeature[] = [
    {
      title: "Single Sign-On (SSO)",
      description: "Streamlined access to all applications with one set of credentials",
      icon: "ri-key-line",
      benefits: ["Improved User Experience", "Reduced Password Fatigue", "Centralized Authentication", "Enhanced Security"],
      image: "https://readdy.ai/api/search-image?query=Professional%20single%20sign-on%20interface%20showing%20unified%20login%20portal%2C%20modern%20authentication%20dashboard%20with%20multiple%20application%20icons%2C%20secure%20user%20access%20management%2C%20clean%20UI%20design%20with%20security%20badges%20and%20user%20profile%20integration&width=500&height=400&seq=sso1&orientation=landscape"
    },
    {
      title: "Multi-Factor Authentication",
      description: "Additional security layers beyond passwords for verified access",
      icon: "ri-shield-user-line",
      benefits: ["Enhanced Security", "Biometric Options", "SMS/Email Verification", "Hardware Tokens"],
      image: "https://readdy.ai/api/search-image?query=Multi-factor%20authentication%20process%20visualization%20showing%20smartphone%20with%20verification%20codes%2C%20biometric%20fingerprint%20scanner%2C%20security%20tokens%2C%20and%20authentication%20flow%20diagram%2C%20modern%20cybersecurity%20interface%20with%20multiple%20verification%20methods&width=500&height=400&seq=mfa1&orientation=landscape"
    },
    {
      title: "Privileged Access Management",
      description: "Controlled access to critical systems and administrative functions",
      icon: "ri-admin-line",
      benefits: ["Privileged Account Security", "Session Recording", "Just-in-Time Access", "Audit Trails"],
      image: "https://readdy.ai/api/search-image?query=Privileged%20access%20management%20dashboard%20showing%20administrative%20user%20permissions%2C%20access%20control%20matrix%2C%20security%20clearance%20levels%2C%20audit%20logs%2C%20and%20privileged%20account%20monitoring%20interface%20with%20security%20badges%20and%20access%20restrictions&width=500&height=400&seq=pam1&orientation=landscape"
    },
    {
      title: "Identity Governance",
      description: "Automated provisioning and compliance management across systems",
      icon: "ri-group-line",
      benefits: ["Automated Provisioning", "Access Reviews", "Compliance Reporting", "Role Management"],
      image: "https://readdy.ai/api/search-image?query=Identity%20governance%20dashboard%20displaying%20user%20lifecycle%20management%2C%20automated%20provisioning%20workflows%2C%20compliance%20reports%2C%20role-based%20access%20controls%2C%20and%20organizational%20hierarchy%20with%20security%20policy%20enforcement%20visualization&width=500&height=400&seq=governance1&orientation=landscape"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = sectionRef.current?.querySelectorAll('.iam-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Identity & Access Management
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive IAM solutions to secure user identities and control access across your organization
          </p>
        </div>

        {/* Hero Visualization */}
        <div className="mb-16 text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20identity%20and%20access%20management%20architecture%20showing%20user%20authentication%20flow%2C%20digital%20identity%20verification%2C%20access%20control%20mechanisms%2C%20holographic%20user%20profiles%2C%20biometric%20authentication%20elements%2C%20futuristic%20IAM%20dashboard%20with%20blue%20and%20cyan%20lighting%20effects&width=1200&height=600&seq=iam3d&orientation=landscape"
              alt="3D IAM Architecture"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* IAM Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {iamFeatures.map((feature, index) => (
            <div
              key={index}
              data-index={index}
              className={`iam-item group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:bg-slate-800/60 transform transition-all duration-700 hover:scale-102 cursor-pointer ${
                visibleItems.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex flex-col lg:flex-row h-full">
                {/* Content */}
                <div className="flex-1 p-8">
                  <div className="flex items-center mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-500 rounded-xl flex items-center justify-center mr-4 group-hover:animate-pulse ${
                      hoveredCard === index ? 'animate-pulse' : ''
                    }`}>
                      <i className={`${feature.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors duration-300">
                      {feature.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div 
                        key={benefitIndex}
                        className="flex items-center text-sm text-gray-300 p-2 bg-slate-700/30 rounded-lg border border-slate-600/30 hover:border-cyan-400/50 transition-colors duration-300"
                      >
                        <i className="ri-check-line text-cyan-400 mr-2"></i>
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className="lg:w-1/2 relative overflow-hidden">
                  <img 
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-64 lg:h-full object-cover group-hover:scale-110 transform transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-slate-900/40 to-transparent"></div>
                  
                  {/* Floating icon */}
                  <div className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center transition-transform duration-300 ${
                    hoveredCard === index ? 'animate-bounce' : ''
                  }`}>
                    <i className={`${feature.icon} text-white`}></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* IAM Benefits Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {[
            { stat: "75%", label: "Reduction in Help Desk Tickets", icon: "ri-customer-service-line" },
            { stat: "90%", label: "Faster User Onboarding", icon: "ri-user-add-line" },
            { stat: "100%", label: "Compliance Visibility", icon: "ri-file-shield-line" }
          ].map((item, index) => (
            <div
              key={index}
              data-index={index + 10}
              className={`iam-item text-center p-8 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl hover:bg-slate-800/60 transform transition-all duration-500 hover:scale-105 cursor-pointer ${
                visibleItems.includes(index + 10) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${(index + 4) * 100}ms` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-cyan-600 to-blue-500 rounded-xl flex items-center justify-center">
                <i className={`${item.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-4xl font-bold text-white mb-2">{item.stat}</div>
              <div className="text-gray-400">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Access Control Workflow */}
        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-white mb-8">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Secure Access Workflow
            </span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Authentication", desc: "Verify user identity", icon: "ri-user-line" },
              { step: "2", title: "Authorization", desc: "Check permissions", icon: "ri-shield-check-line" },
              { step: "3", title: "Access Control", desc: "Grant/deny access", icon: "ri-key-line" },
              { step: "4", title: "Audit & Monitor", desc: "Log and review", icon: "ri-eye-line" }
            ].map((workflow, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-r from-cyan-600 to-blue-500 rounded-full flex items-center justify-center group-hover:animate-pulse">
                    <i className={`${workflow.icon} text-xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {workflow.step}
                  </div>
                </div>
                <h4 className="font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors duration-300">
                  {workflow.title}
                </h4>
                <p className="text-gray-400 text-sm">{workflow.desc}</p>
                
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <i className="ri-arrow-right-line text-cyan-400 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
